import React, { useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UserContext } from '../Reducers/UserContext';
import { useSecureConfigContext } from '../SecureConfig/SecureConfigProvider';
import Lockr from 'lockr';
import { authorizeGoogle } from '../../api';
import { Spin, Button, message } from 'antd';
import { LoadingOutlined, LoginOutlined } from '@ant-design/icons';

const antIcon = (
    <LoadingOutlined
        style={{
            fontSize: 24,
        }}
        spin
    />
);

// Inner component that uses Auth0 hooks
const Auth0LoginInner = ({ disabled = false }) => {
    const { isLoggedIn, setLogger } = useContext(UserContext);
    const [logoutCalled, setLogoutCalled] = useState(false);

    // Always call useAuth0 hook at the top level
    const { loginWithRedirect, logout, isAuthenticated, user, isLoading } = useAuth0();

    const handleLogin = () => {
        if (loginWithRedirect) {
            loginWithRedirect();
        }
    };

    useEffect(() => {
        const handleAuth = async () => {
            if (isAuthenticated && user && !isLoading && !isLoggedIn?.user?.auth0LogOut) {
                try {
                    const data = await authorizeGoogle({
                        usr: user.email,
                        res: user,
                    });

                    setLogger({
                        active: true,
                        user: {
                            role: 'guest',
                            name: user.email,
                            subscriber_id: data['usr_exst']['id'],
                            entry: new Date(),
                            auth0LogOut: false,
                            authMethod: 'auth0',
                        },
                    });
                } catch (err) {
                    console.error('Auth0 post-login API call failed:', err);
                    message.error('Login failed. Please try again.');
                    logout({ openUrl: false });
                }
            }
        };

        if (logoutCalled) {
            Lockr.flush();
            setLogger({
                active: false,
                user: { role: 'guest', auth0LogOut: false },
            });
            return;
        }

        if (isLoggedIn?.user?.auth0LogOut && logout) {
            setLogoutCalled(true);
            logout({ logoutParams: { returnTo: window.location.origin } });
        }

        handleAuth();
    }, [isAuthenticated, user, isLoading, isLoggedIn?.user?.auth0LogOut, logoutCalled, logout]);

    if (isAuthenticated) {
        return (
            <div style={{ textAlign: 'center' }}>
                <Spin indicator={antIcon} />
            </div>
        );
    }

    if (disabled) {
        return (
            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <Spin size="small" />
                <span
                    style={{
                        marginLeft: '8px',
                        color: '#999',
                        fontStyle: 'italic',
                        fontSize: '12px',
                    }}
                >
                    🔄 Auth0 Login
                </span>
            </div>
        );
    }

    return (
        <Button type="primary" icon={<LoginOutlined />} onClick={handleLogin} block>
            Login with Auth0
        </Button>
    );
};

// Main component that handles Auth0Provider availability
const Auth0Login = ({ disabled = false }) => {
    const { hasSecureConfig } = useSecureConfigContext();

    // If secure config is not available, show loading state
    if (!hasSecureConfig()) {
        return (
            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <Spin size="small" />
                <span
                    style={{
                        marginLeft: '8px',
                        color: '#999',
                        fontStyle: 'italic',
                        fontSize: '12px',
                    }}
                >
                    🔄 Auth0 Login
                </span>
            </div>
        );
    }

    // Try to render Auth0LoginInner, fall back to loading if Auth0Provider is not available
    try {
        return (
            <div style={{ marginBottom: '4px' }}>
                <Auth0LoginInner disabled={disabled} />
            </div>
        );
    } catch (error) {
        console.warn('Auth0Provider not available:', error.message);
        return (
            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <Spin size="small" />
                <span
                    style={{
                        marginLeft: '8px',
                        color: '#999',
                        fontStyle: 'italic',
                        fontSize: '12px',
                    }}
                >
                    🔄 Auth0 Login
                </span>
            </div>
        );
    }
};

export default Auth0Login;
